import { Location } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IHeartbeatResult } from '@interfaces/heartbeat-result';
import { IUserInformation } from '@interfaces/userInformation';
import * as UserInfoActions from '@state/actions/user-profile.actions';
import { selectUserInformation } from '@state/selectors/user-profile.selectors';
import { TapAppStore } from '@state/tap-state';
import {
  NullValidationHandler,
  OAuthErrorEvent,
  OAuthService,
} from 'angular-oauth2-oidc';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { CacheBuster, globalCacheBusterNotifier } from 'ts-cacheable';
import { ActiveFilter } from '../modules/tank-quick-filters/components/global-tank-filters/tank-quick-filters.component';
import { AppSettings } from '../shared/Constants/sharedConstants';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { AppDialogService } from './app-dialog.service';
import { EXPIRE_SESSION_ON_401 } from './authinterceptor.service';
import { AUTH_CONFIG, SecurityPolicies } from './auth-variables';
import { LogService } from './log.service';
import { OrganizationFeaturesService } from './organization-features.service';
import { OrganizationService } from './organization.service';
import { SignalRService } from './signalr.service';
import { TabOrganizationService } from './tab-organization.service';
import { TankFilterService } from './tank-filter.service';
import { TokenRefreshService } from './token-refresh.service';
import { TokenService } from './token.service';

(window as any).global = window;

export interface ILoginPageModel {
  themePathPrefix: string;
  font: string;
  colors: string;
  desktopLogo: string;
  logoBackgroundColor: string;
  mobileLogo: string;
  logoHyperlink: string;
  logToSiteUsageTracker: boolean;
  siteTrackerUrl: string;
  siteTrackerSiteId: string;
  appleStoreLink: string;
  googleStoreLink: string;
  loginData: ILoginData;
}

export interface ILoginData {
  username: string;
  password: string;
  rememberMe: boolean;
  error: string;
}

const RoleNames = {
  SuperUser: 'SuperUser',
  PrimaryContact: 'PrimaryContact',
};

const FeatureNames = {
  Theming: 'Theming',
  AnomalyDetection: 'Anomaly Detection',
};

const PermissionNames = {
  HasWebAccess: 'HasWebAccess',
  ManageThemes: 'ManageThemes',
  ManageOrganizations: 'ManageOrganizations',
  ManagePremiumServices: 'ManagePremiumServices',
  ManageAlarms: 'ManageAlarms',
  ManageTanks: 'ManageTanks',
  ManageCallSchedule: 'ManageCallSchedule',
  ManageUsers: 'ManageUsers',
  ManageLocations: 'ManageLocations',
  ManageProducts: 'ManageProducts',
  ManageTankProfile: 'ManageTvp',
  ManageDiagnostic: 'ManageDiagnostic',
};

export interface IResetPasswordResponse {
  success: boolean;
  errorMessages: string[];
}

/** Shape of the Authenticate/token response - TL.Portal.API.DTO.LoginResult, camelCased. */
interface IAuthenticateTokenResult {
  success: boolean;
  error: string;
  token?: {
    body: string;
    expiryInUtc: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public securityPolicies: any = SecurityPolicies;
  private _userBehaviorSubject: BehaviorSubject<IUserInformation>;
  private userId: number;
  private debugImpersonatingId: any;
  public loggedIn$: Subject<boolean> = new Subject<boolean>();
  private sessionExpiring = false;
  private organizationChangesSubscribed = false;
  private announcedOrganizationId: number | null = null;

  constructor(
    public router: Router,
    private oauthService: OAuthService,
    private restClient: DSRESTClient,
    private cookieService: CookieService,
    private appStore: TapAppStore,
    private logger: LogService,
    private location: Location,
    private ofService: OrganizationFeaturesService,
    private organizationService: OrganizationService,
    private appDialogService: AppDialogService,
    private tankFilterService: TankFilterService,
    private signalRService: SignalRService,
    private tokenService: TokenService,
    private tokenRefreshService: TokenRefreshService,
    private translate: TranslateService,
    private tabOrganization: TabOrganizationService,
  ) {}

  public subscribeToLogoutNotifications() {
    this.logger.log('Subscribing to logout notifications via SignalR...');
    this.signalRService
      .getLogoutNotifications()
      .pipe(
        filter((n) => n?.data?.action === 'logout'),
        take(1),
      )
      .subscribe((notification) => {
        this.logger.log(
          'Received logout notification via SignalR:',
          notification,
        );
        if (notification?.data?.action === 'logout') {
          this.processLogout({ forceLogout: true });
          this.navigateToLogin();
        }
      });
  }

  /**
   * The counterpart to subscribeToLogoutNotifications, for the selected organization.
   *
   * The organization is one row shared by every tab, window and device this user has open - and by
   * the legacy app - while the id that scopes their queries is a claim baked into each session's
   * own token. Nothing about a switch made elsewhere reaches this tab on its own; it carries on
   * querying the organization it was opened in, while several backend services read the shared row
   * instead of the claim and answer for the new one. The two disagree with no error anywhere.
   *
   * The server excludes the connection that asked for the change, so this never fires in the tab
   * that made it. Not `take(1)` like the logout subscription: the organization can move more than
   * once in a session, and each move needs handling.
   */
  public subscribeToOrganizationChangeNotifications() {
    // Unlike the logout subscription this one is not bounded by take(1) - the organization can move
    // more than once in a session - so it has to be guarded against stacking. AuthService is root
    // provided and outlives AuthShellComponent, which calls this from ngAfterViewInit; a shell
    // rebuilt without a page reload would otherwise add a second subscription, and the user would
    // be told twice and refetch their profile twice for one change.
    if (this.organizationChangesSubscribed) return;
    this.organizationChangesSubscribed = true;

    this.logger.log('Subscribing to organization change notifications via SignalR...');
    this.signalRService
      .getOrganizationChangeNotifications()
      .subscribe((notification) => {
        const organizationId = notification?.data?.organizationId;
        if (!organizationId) return;

        this.applyExternalOrganizationChange(
          organizationId,
          notification?.data?.organizationName,
        );
      });
  }

  /**
   * Handles an organization change made somewhere else, per AppSettings.csoChangeBehaviour.
   *
   * Under `silent` nothing happens at all: the tabs are simply independent. Under `notify` the tab
   * is likewise left as it is - same credential, same claim, same data - and the user is told why
   * another tab disagrees with this one. Both are coherent rather than a compromise: the CSO claim
   * on this session's own token is the only thing any scoping or authorization decision reads, so a
   * tab on an older claim is internally consistent, it is simply looking at a different organization
   * than another tab is.
   *
   * Under `converge` the tab follows: a credential carrying the new organization, a re-read profile
   * and busted caches, and then the same message - because what is on screen was still painted for
   * the organization they have just left.
   *
   * Reloading is the user's call either way. Doing it for them destroys unsaved work they cannot
   * get back, and there is no way from here to know whether a form is half-filled.
   */
  public applyExternalOrganizationChange(
    organizationId: number,
    organizationName: string,
  ): void {
    // Tabs are independent and nobody is told. Checked before anything else, including the
    // already-announced bookkeeping, so that flipping the knob to `notify` at runtime announces the
    // next report rather than suppressing it as already seen.
    if (AppSettings.csoChangeBehaviour === 'silent') return;

    const currentOrganizationId = this._userBehaviorSubject?.value
      ?.lastSelectedOrganizationId;

    // Already there. Two tabs can sit on the same organization, and a tab that refreshed for its own
    // reasons has picked the change up already.
    if (organizationId && organizationId === currentOrganizationId) return;

    // Under `notify` nothing here resolves the divergence, so the heartbeat keeps finding it and
    // reporting it on every beat. Without this the user is told again every few minutes, forever,
    // about one change they have already seen. Keyed on the organization so a SECOND move, or a move
    // back, still gets announced.
    if (organizationId && organizationId === this.announcedOrganizationId) return;
    this.announcedOrganizationId = organizationId;

    if (AppSettings.csoChangeBehaviour === 'notify') {
      this.announceOrganizationChange(organizationId, organizationName);
      return;
    }

    // Before the refresh, not after: the refresh sends this tab's intent, so leaving it unset would
    // have the server hand back a credential for the organization this tab was ALREADY in and
    // converge would quietly stop converging.
    this.tabOrganization.set(organizationId);

    this.tokenRefreshService
      .refresh()
      .pipe(
        take(1),
        switchMap(() => {
          globalCacheBusterNotifier.next();
          return this.getUserInformation(true);
        }),
        take(1),
      )
      .subscribe({
        next: (u: IUserInformation) => {
          this.appStore.store.dispatch(new UserInfoActions.GetUserInfo(u));
          this.announceOrganizationChange(
            organizationId,
            organizationName || u?.lastSelectedOrganizationName,
          );
        },
        error: () => {
          // The credential could not be rebuilt or the profile could not be read, so this tab did
          // not converge after all. Say so anyway and offer the reload, which bootstraps a fresh
          // token through the app initializer.
          this.announceOrganizationChange(organizationId, organizationName);
        },
      });
  }

  private announceOrganizationChange(
    organizationId: number,
    organizationName: string,
  ): void {
    // Says what actually happened. This session has already moved - token and profile both - so
    // claiming the page "is showing the previous organization" contradicted the organization name
    // in the header, which updates from the same dispatch above. What is genuinely stale is the
    // data already painted, and reloading is what fixes that.
    //
    // Both keys live in the CSO string-resource group; see
    // TL.DatabaseScripts/DML/SBTMTAPWP-2176/cso-changed-elsewhere-string-resources.sql. That
    // script has to be applied wherever this code runs or the snackbar shows the raw key.
    const message = organizationName
      ? this.translate.instant('CSO.OrganizationChangedElsewhere', {
          organization: organizationName,
        })
      : this.translate.instant('CSO.OrganizationChangedElsewhereUnknown');

    this.appDialogService
      .showActionMessage(message, this.translate.instant('CSO.ReloadPage'))
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        // Pressing Reload IS the user accepting the change, so it is an intent change and has to be
        // recorded before the page goes. Without this the reload sends the organization this tab was
        // already in and lands them exactly where they started - the button would look broken.
        this.tabOrganization.set(organizationId);
        window.location.reload();
      });
  }

  /**
   * The other half of the guardrail comment on login() above: this already targets a real tenant
   * and App Registration (AUTH_CONFIG in auth-variables.ts, environment.ts's sso/userdetail) via a
   * proper Authorization Code + PKCE flow, but nothing calls this method - it isn't wired into app
   * bootstrap. This, not Authenticate/token, is where an Entra ID migration should resume from.
   */
  public configureWithNewConfigApi() {
    this.oauthService.configure(AUTH_CONFIG);
    this.oauthService.setStorage(localStorage);
    this.oauthService.tokenValidationHandler = new NullValidationHandler();
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.error(event);
      } else {
        console.warn(event);
      }
    });
    return this.oauthService
      .loadDiscoveryDocumentAndLogin()
      .then((loggedIn) => {
        if (loggedIn) {
          this.loadUserProfile().subscribe(this.processUserLoaded.bind(this));
        }
      });
  }

  @CacheBuster({
    isInstant: true,
  })
  public updateLastSelectedOrganization(
    orgId: number,
    overrideFilter: boolean = false
  ): Observable<IUserInformation> {
    globalCacheBusterNotifier.next();
    const endpoint = 'UserProfile/UpdateLastSelectedOrganization';
    return this.restClient.get(`${endpoint}/${orgId}`).pipe(
      switchMap(() => this.getUserInformation(true)),
      take(1),
      tap({
        next: (u: IUserInformation) => {
          this.logger.log('updateLastSelectedOrganization next: ', u);
          this.appStore.store.dispatch(new UserInfoActions.GetUserInfo(u));

          // This tab's own choice, so it becomes this tab's intent - which is what keeps it here
          // after a reload instead of adopting whatever another tab picked later.
          this.tabOrganization.set(u?.lastSelectedOrganizationId);

          if (overrideFilter)
            return;

          this.tankFilterService
            .setLastSelectedTankQuickFilter(
              ActiveFilter,
              null,
              this.constructor.name,
            )
            .then(() => {});
        },

        error: () => {
          this.appDialogService.showErrorMessage(
            'An error occurred while updating your organization.',
          );
        },
      }),
    );
  }

  public canSelectOrganizationForCso(orgId: number): Observable<boolean> {
    const endpoint = 'UserProfile/CanSelectOrganizationForCso';
    return this.restClient.getDirect<boolean>(`${endpoint}/${orgId}`);
  }

  public updateLastSelectedTankProfile(tankProfileId: number, orgId: number) {
    const endpoint = 'UserProfile/UpdateLastSelectedTankProfile';
    return this.restClient.get(`${endpoint}/${tankProfileId}/${orgId}`);
  }

  public setUserId(id: number) {
    this.userId = id;
    this.cookieService.delete('USERID'); //delete this cookie, it's not used anymore
  }
  public getUserId(): string {
    return this.userId.toString();
  }

  public getUserInformation(
    refreshCacheFromServer: boolean = false,
  ): Observable<IUserInformation> {
    this.logger.log(
      'getUserInformation refresh Cache From Server: ' + refreshCacheFromServer,
    );
    //get the user's information and store locally for security lookup
    //return's an observable of the internal user store.
    let userInformation: Observable<IUserInformation>;

    if (refreshCacheFromServer) {
      userInformation = this.getUserFromServer();
    } else {
      userInformation = this.loadUserProfile();
    }

    return userInformation.pipe(
      switchMap((userInfo) => {
        this.processUserLoaded(userInfo);
        return this._userBehaviorSubject;
      }),
    );
  }

  public getOriginatingUser(): number {
    return this._userBehaviorSubject?.getValue()?.originatingUserId ?? 0;
  }

  public getImpersonatingUserId(): number {
    if (this.debugImpersonatingId) {
      return Number(this.debugImpersonatingId);
    }
    return this._userBehaviorSubject?.getValue()?.impersonatingUserId ?? 0;
  }

  public getIsImpersonating(): boolean {
    return this._userBehaviorSubject?.getValue()?.isImpersonating ?? false;
  }

  /**
   * Password-grant (ROPC) login against Authenticate/token - functional, but a deliberately
   * interim foundation. It's the same endpoint the mobile tank-installer app already used, not a
   * new one built for this migration, and it isn't the shape to build an Entra ID login on:
   * Entra never sees an interactive sign-in here, so MFA/Conditional Access/passwordless can't
   * apply. A real Entra ID login is a redirect-based Authorization Code + PKCE flow instead - see
   * configureWithNewConfigApi() below and AUTH_CONFIG in auth-variables.ts, which already points
   * at a real tenant and App Registration (environment.ts) and just isn't wired up to anything
   * yet. That's where a future migration should pick up, not here.
   */
  public login(
    userName: string,
    password: string,
  ): Observable<ILoginPageModel> {
    // Query string, not a JSON body, because that's the existing contract of Authenticate/token -
    // it already has another consumer (the mobile tank-installer app, see the e2e Authenticate API
    // tests), so its signature isn't ours to change as part of this migration.
    const endPoint = `Authenticate/token?username=${encodeURIComponent(userName)}&password=${encodeURIComponent(password)}`;
    this.sessionExpiring = false;
    return this.restClient.postForResponse<IAuthenticateTokenResult>(endPoint, {}).pipe(
      tap((result) => {
        if (result?.success && result.token?.body) {
          this.tokenService.setToken(result.token.body, result.token.expiryInUtc);
        }
      }),
      map((result) => this.toLoginPageModel(userName, result)),
    );
  }

  /**
   * Adapts the Authenticate/token response (LoginResult) into the ILoginPageModel shape
   * login.component.ts already expects, so the login-cookie-era UI needed no changes here. The
   * theme fields were already server-defaults-only under the old cookie login (see
   * LoginController.SetupReplyModel) and stay that way.
   */
  private toLoginPageModel(userName: string, result: IAuthenticateTokenResult): ILoginPageModel {
    return {
      themePathPrefix: '',
      font: '',
      colors: '',
      desktopLogo: '',
      logoBackgroundColor: '',
      mobileLogo: '',
      logoHyperlink: '',
      logToSiteUsageTracker: false,
      siteTrackerUrl: '',
      siteTrackerSiteId: '',
      appleStoreLink: '',
      googleStoreLink: '',
      loginData: {
        username: userName,
        password: '',
        rememberMe: false,
        error: result?.success ? '' : (result?.error ?? 'username or password is invalid'),
      },
    };
  }

  public logout(options?: any): void {
    this.processLogout(options);
    this.navigateToLogin();
  }

  private navigateToLogin() {
    this.router.navigate(['login']);
  }

  private processLogout(_options): void {
    // Fire the server-side logout - and the SignalR broadcast to the user's other sessions it
    // triggers - before clearing the token below. Once cleared, this request would go out with no
    // Authorization header, and UserSecurity.UserId() would have nothing to broadcast a logout
    // for. This didn't matter under cookies, since sessionStorage.clear() never touched the cookie.
    const logoutUrl = `logout`;
    this.restClient.get(logoutUrl).pipe(take(1)).subscribe();

    // Separate call, because the refresh cookie is path-scoped to Authenticate/refresh and the
    // browser will not apply a cross-origin deletion sent from anywhere else - see
    // TokenRefreshService.revoke. Without it a logout under ng serve leaves the credential in place
    // and the next reload bootstraps a new token from it.
    this.tokenRefreshService.revoke().pipe(take(1)).subscribe();

    // Forget which organization this tab was in. sessionStorage outlives a logout - it is cleared
    // only when the tab closes - so without this the next person to sign in here would be put into
    // the previous user's organization on their first reload, and the server would honour it if they
    // happen to have access.
    this.tabOrganization.clear();

    // Open dialogs live in the CDK overlay container on <body>, so navigating to login does not
    // touch them - they stay pinned over the login page, and the ones opened with `disableClose`
    // (the bulk tank config wizard, the settings panel) cannot be dismissed by hand either. This
    // runs for every way a session ends: the logout button, an expired session, and the SignalR
    // logout broadcast all come through here.
    this.appDialogService.dismissAllOverlays();

    //otherwise, if the authenticated user is the one logging out,
    //clean up their session and log out
    this.oauthService.logOut();
    localStorage.clear();
    sessionStorage.clear();
    this.tokenService.clearToken();
    this.appStore.logout();
    // The theme is a property of the URL the visitor arrived on, not of who they are, so it
    // deliberately survives logout. The remembered slug lives in an HttpOnly cookie, which
    // deleteAll cannot see, so it survives this too. Pushing null at themeConfig$ here only ever
    // looked like a reset - the service filters nulls out.
    this.cookieService.deleteAll();
  }

  /**
   * Called by AuthInterceptor when a request that opted into EXPIRE_SESSION_ON_401 is rejected with
   * 401 - in practice the heartbeat. The auth cookie is a sliding cookie with an absolute cap, so
   * a 401 there means the session is dead server-side and cannot be refreshed - clear it down and
   * send the user to login with a reason, rather than leaving a dead page on screen until the next
   * full reload. A 401 on an ordinary API call is an authorization failure and does not come here.
   */
  public expireSession(): void {
    if (this.sessionExpiring) return;
    this.sessionExpiring = true;

    this.processLogout({ forceLogout: true });
    this.router.navigate(['login'], { queryParams: { reason: 'expired' } });
  }

  public GetBuildInfo(): Observable<any> {
    const endPoint = 'UserProfile/GetBuildInfo';
    return this.restClient.get(endPoint);
  }

  public resetUserPassword(
    userName: string,
    email: string,
  ): Observable<IResetPasswordResponse> {
    const endPoint = 'user/reset-password';
    return this.restClient.postForResponse(endPoint, {
      userName: userName,
      email: email,
    });
  }

  public silentRefresh() {
    return this.oauthService.silentRefresh();
  }

  //Process an Authorization Policy against the UserInformation.
  //return an Observable of boolean (Policy Pass Result).
  public passesPolicyOf(
    securityPolicy: SecurityPolicies | SecurityPolicies[],
    options: any = { data: null },
  ): Observable<boolean> {
    //if the security policy is NOT an array return the result of passesPolicyOf
    if (!Array.isArray(securityPolicy)) {
      return this.processPolicy(securityPolicy, options);
    }

    //loop through the array of security policies, and validate each one. Combine the results and return an observable.
    return this.processPolicyArray(securityPolicy, options);
  }

  private processPolicyArray(
    securityPolicy: SecurityPolicies[],
    options: any,
  ): Observable<boolean> {
    return this.getUserInformation().pipe(
      // Only do this once.
      take(1),

      // Process policy logic
      switchMap(() => {
        return forkJoin(
          securityPolicy.map((sp) => this.processPolicy(sp, options)),
        ).pipe(
          map((results: boolean[]) => results.every((r) => r)), // Use map for boolean transformation
        );
      }),
    );
  }

  private processPolicy(
    securityPolicy: SecurityPolicies,
    options: any,
  ): Observable<boolean> {
    return this.getUserInformation().pipe(
      //only do this once.
      take(1),

      //process policy logic
      switchMap((userInfo) => {
        switch (securityPolicy) {
          // case SecurityPolicies.BlockAutomaticRedirectToNewUI :
          //   return of(
          //     userInfo?.roles.includes(RoleNames.SuperUser) ||
          //       userInfo?.permissions.includes(PermissionNames.ManageUsers),
          //   );
          case SecurityPolicies.HasWebAccess:
            return of(
              userInfo?.permissions?.includes(PermissionNames.HasWebAccess),
            );

          case SecurityPolicies.RequireSuperUser:
            return of(userInfo?.roles?.includes(RoleNames.SuperUser));

          case SecurityPolicies.RequiresTheming:
            if (!options.data) {
              return of(userInfo?.features?.includes(FeatureNames.Theming));
            }

            const orgDetails: any = options?.data;
            return of(
              orgDetails?.features?.findIndex(
                (f) => f.featureName === FeatureNames.Theming,
              ) > -1,
            );

          case SecurityPolicies.RequiresPrimaryContact:
            return of(
              userInfo?.roles?.includes(RoleNames.PrimaryContact) ||
                userInfo?.roles?.includes(RoleNames.SuperUser),
            );

          case SecurityPolicies.RequiresManageThemes:
            return of(
              userInfo?.permissions?.includes(PermissionNames.ManageThemes) ||
                userInfo?.roles?.includes(RoleNames.PrimaryContact) ||
                userInfo?.roles?.includes(RoleNames.SuperUser),
            );
          case SecurityPolicies.RequiresManageOrganizationsOrPremiumServices:
            this.logger.debug(
              'RequiresManageOrganizationsOrPremiumServices ===================================',
              userInfo,
            );
            return of(
              userInfo?.permissions?.includes(
                PermissionNames.ManageOrganizations,
              ) ||
                userInfo?.permissions?.includes(
                  PermissionNames.ManagePremiumServices,
                ) ||
                userInfo?.roles?.includes(RoleNames.SuperUser),
            );
          case SecurityPolicies.RequiresManageAlarms:
            return of(
              userInfo?.permissions?.includes(PermissionNames.ManageAlarms),
            );
          case SecurityPolicies.RouteOrgHasAnomalyDetection: {
            //get the orgId from the route using ng Location
            const routeOrgId = this.getRouteOrgId();
            return this.ofService
              .getOrganizationFeatures(routeOrgId)
              .pipe(
                switchMap((orgFeatures) =>
                  of(
                    orgFeatures?.features?.some(
                      (f) =>
                        f.name === FeatureNames.AnomalyDetection &&
                        f.value === true,
                    ),
                  ),
                ),
              );
          }
          case SecurityPolicies.RouteOrgIsBillagleOrg: {
            const routeOrgId = this.getRouteOrgId();
            //debugger; // 5
            return this.organizationService.getOrganization(routeOrgId).pipe(
              map((org) => {
                return org?.organizationDetails?.isBillableOrg ?? false;
              }),
            );
          }
          case SecurityPolicies.RequiresThemingAndHasManageOrgs: {
            const hasManageOrgs = userInfo.permissions.includes(
              PermissionNames.ManageOrganizations,
            );

            const hasManagePremiumServices = userInfo.permissions.includes(
              PermissionNames.ManagePremiumServices,
            );
            //user must have manage orgs permission to manage themes
            if (!hasManageOrgs && !hasManagePremiumServices) return of(false);

            //Org must have theming enabled
            const routeOrgId = this.getRouteOrgId();
            return this.organizationService.getOrganization(routeOrgId).pipe(
              map((org) => {
                return org.features.some(
                  (f) =>
                    f.featureName === FeatureNames.Theming && f.active === true,
                );
              }),
            );
          }
          case SecurityPolicies.RequiresManageTanks: {
            const hasManageTanks = userInfo.permissions.includes(
              PermissionNames.ManageTanks,
            );
            return of(hasManageTanks);
          }
          case SecurityPolicies.RequireSuperUserOrManageOrgs: {
            return of(
              userInfo?.permissions?.includes(
                PermissionNames.ManageOrganizations,
              ) || userInfo?.roles?.includes(RoleNames.SuperUser),
            );
          }
          case SecurityPolicies.RequiresManageLocations: {
            const hasManageLocations = userInfo.permissions.includes(
              PermissionNames.ManageLocations,
            );
            return of(hasManageLocations);
          }
          case SecurityPolicies.RequiresManageCallSchedule: {
            return of(
              userInfo?.permissions.includes(
                PermissionNames.ManageCallSchedule,
              ),
            );
          }
          case SecurityPolicies.RequiresManageUsers: {
            return of(
              userInfo?.roles.includes(RoleNames.SuperUser) ||
                userInfo?.permissions.includes(PermissionNames.ManageUsers),
            );
          }
          case SecurityPolicies.RequiresManageOrganizations: {
            const hasManageOrganizations =
              userInfo.permissions.includes(
                PermissionNames.ManageOrganizations,
              ) || userInfo?.roles.includes(RoleNames.SuperUser);
            return of(hasManageOrganizations);
          }
          case SecurityPolicies.CanMoveTank: {
            const canMoveTank =
              (userInfo.permissions.includes(PermissionNames.ManageTanks) &&
                userInfo.permissions.includes(
                  PermissionNames.ManageLocations,
                )) ||
              userInfo?.roles.includes(RoleNames.SuperUser);
            return of(canMoveTank);
          }
          case SecurityPolicies.RequiresManageProducts: {
            return of(
              userInfo?.permissions.includes(PermissionNames.ManageProducts),
            );
          }
          case SecurityPolicies.RequiresManageTankProfile: {
            return of(
              userInfo?.permissions.includes(
                PermissionNames.ManageTankProfile,
              )
            );
          }
          case SecurityPolicies.RequiresManageDiagnostics: {
            return of(
              userInfo?.permissions?.includes(PermissionNames.ManageDiagnostic),
            );
          }
        }
      }),
    );
  }

  private getRouteOrgId() {
    let routeOrgId = 0;
    let pathSegements = this.location.path().toLowerCase().split('/');

    let valueIX = pathSegements.findIndex((ps) => ps === 'organization') + 1;
    routeOrgId = Number.parseInt(pathSegements[valueIX]);
    return routeOrgId;
  }

  private loadUserProfile(): Observable<IUserInformation> {
    const cached = this._userBehaviorSubject?.getValue();
    if (cached && cached.userId > 0) {
      this.logger.info('loadUserProfile behavior subject served=================', cached);
      return of(cached);
    }

    return this.appStore.store.select(selectUserInformation).pipe(
      switchMap((userInfo) => {
        //if the state store already has the user information
        if (userInfo && userInfo.userId > 0) {
          this.logger.info(
            'loadUserProfile state store served=================',
            userInfo,
          );
          return of(userInfo);
        } else {
          this.logger.info(
            'loadUserProfile getting from end point===================================',
            userInfo,
          );

          //get the user's infor from the end point.
          return this.getUserFromServer();
        }
      }),
    );
  }

  private processUserLoaded(userInfo: IUserInformation) {
    this.setUserId(userInfo.userId);

    // Seeds a tab that has no intent yet - a fresh sign-in, or the first load after this was
    // introduced - from whatever organization the session actually came back scoped to. Deliberately
    // only when unset: overwriting here would let a profile read triggered by something else quietly
    // move a tab that had already chosen, and the server has already had the final say on what this
    // session is scoped to by the time this runs.
    if (this.tabOrganization.get() === null) {
      this.tabOrganization.set(userInfo?.lastSelectedOrganizationId);
    }

    if (this._userBehaviorSubject) {
      if (this._userBehaviorSubject.getValue() !== userInfo) {
        this._userBehaviorSubject.next(userInfo);
      }
    } else {
      this._userBehaviorSubject = new BehaviorSubject<IUserInformation>(userInfo);
    }
  }

  @CacheBuster({
    isInstant: true,
  })
  private getUserFromServer(): Observable<IUserInformation> {
    globalCacheBusterNotifier.next();
    const endpoint = 'UserProfile';
    return this.restClient.getDirect<IUserInformation>(`${endpoint}`).pipe(
      tap((userInfo) => {
        if (!userInfo || userInfo.userId === 0) {
          this.navigateToLogout();
        }
        if (userInfo.mustChangePassword) {
          // Cache before redirecting: the navigation cancels the guard that is subscribed here, so
          // without this the redirected navigation finds no profile, fetches again, and loops.
          this.processUserLoaded(userInfo);
          this.navigateToMyAccount();
        }
      }),
    );
  }

  navigateToLogout() {
    this.router.navigate(['logout']);
  }

  navigateToMyAccount() {
    this.router.navigate(['my-account']);
  }
  private heartbeatUrl = 'Authenticate/heartbeat';
  /**
   * Asserts the auth cookie and nothing else, so its 401 can only have come from the cookie scheme
   * itself - the one request in the app where a 401 means the session is over. Hence the opt-in.
   */
  public heartbeat(): Observable<IHeartbeatResult> {
    return this.restClient.getDirect<IHeartbeatResult>(
      this.heartbeatUrl,
      new HttpContext().set(EXPIRE_SESSION_ON_401, true),
    );
  }
}
